import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BookingCard } from '@/components/shared/BookingCard';
import { Calendar } from 'lucide-react';

interface UserBookingsTabProps {
  userId: string;
}

interface Booking {
  id: number;
  service_id?: number | null;
  start_time?: string | null;
  end_time?: string | null;
  status?: string | null;
  services?: {
    id: number;
    name: string;
  } | null;
  customer_id?: string | null;
  note?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  customer_address?: string | null;
  created_at: string;
}

const UserBookingsTab: React.FC<UserBookingsTabProps> = ({ userId }) => {
  const { data: bookings, isLoading } = useQuery({
    queryKey: ['user-bookings', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          service_id,
          start_time,
          end_time,
          status,
          note,
          customer_name,
          customer_email,
          customer_phone,
          customer_address,
          created_at,
          services (
            id,
            name
          )
        `)
        .eq('customer_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Booking[];
    }
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (!bookings || bookings.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No bookings found</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {bookings.map((booking) => (
        <BookingCard
          key={booking.id}
          booking={booking}
        />
      ))}
    </div>
  );
};

export default UserBookingsTab;